import { useQueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { type ComponentType, lazy, Suspense, useEffect, useMemo, useRef } from 'react';
import { type ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation, useParams } from 'react-router-dom';

import { Invitacion, NuevaClave } from './auth/Acceso';
import { Login } from './auth/Login';
import { SessionProvider, useSesionActiva, useSession } from './auth/session';
import { Shell } from './layout/Shell';
import { Casos } from './screens/bandeja/Casos';
import { Conocimiento } from './screens/bandeja/Conocimiento';
import { Correos } from './screens/bandeja/Correos';
import { Contactos } from './screens/Contactos';
import { EmpresaFicha } from './screens/EmpresaFicha';
import { Hoy } from './screens/Hoy';
import { Llamadas } from './screens/Llamadas';
import { PersonaFicha } from './screens/PersonaFicha';
import { Tareas } from './screens/Tareas';
import { claveDeSesion, opcionesPersistencia } from './lib/cachePersistente';
import { Skeleton } from './ui/bits';
import { ErrorDePantalla, NoEncontrado } from './ui/Fallo';

// Pantallas que se bajan al abrirlas (archivo aparte): la primera carga solo trae Hoy, la Bandeja y Contactos.
function diferida<K extends string>(cargar: () => Promise<Record<K, ComponentType>>, nombre: K) {
  const Perezosa = lazy(async (): Promise<{ default: ComponentType }> => ({ default: (await cargar())[nombre] }));
  return function Diferida() {
    return (
      <Suspense
        fallback={
          <div style={{ padding: 24 }} aria-busy="true">
            <Skeleton rows={6} />
          </div>
        }
      >
        <Perezosa />
      </Suspense>
    );
  };
}
const Administracion = diferida(() => import('./screens/Administracion'), 'Administracion');
const Agenda = diferida(() => import('./screens/Agenda'), 'Agenda');
const Ajustes = diferida(() => import('./screens/Ajustes'), 'Ajustes');
const Oportunidades = diferida(() => import('./screens/Oportunidades'), 'Oportunidades');
const Bolsas = diferida(() => import('./screens/ventas/Bolsas'), 'Bolsas');
const MisHoras = diferida(() => import('./screens/ventas/MisHoras'), 'MisHoras');
const HorasEquipo = diferida(() => import('./screens/ventas/HorasEquipo'), 'HorasEquipo');
const ResumenVentas = diferida(() => import('./screens/ventas/Resumen'), 'ResumenVentas');
const Licencias3cx = diferida(() => import('./screens/ventas/Licencias3cx'), 'Licencias3cx');
const Renovaciones3cx = diferida(() => import('./screens/ventas/Licencias3cx'), 'Renovaciones3cx');
const Proyectos = diferida(() => import('./screens/ventas/Proyectos'), 'Proyectos');
const CasosReporte = diferida(() => import('./screens/reportes/CasosReporte'), 'CasosReporte');
const AreasReporte = diferida(() => import('./screens/reportes/CasosReporte'), 'AreasReporte');
const HorasReporte = diferida(() => import('./screens/reportes/HorasReporte'), 'HorasReporte');
const BolsasReporte = diferida(() => import('./screens/reportes/BolsasReporte'), 'BolsasReporte');
const CorreoReporte = diferida(() => import('./screens/reportes/CorreoReporte'), 'CorreoReporte');
const Reportes3cx = diferida(() => import('./screens/reportes/tres-cx/Reportes3cx'), 'Reportes3cx');
const Campanas = diferida(() => import('./screens/marketing/Campanas'), 'Campanas');
const Supervisor = diferida(() => import('./screens/Supervisor'), 'Supervisor');
const Tarificador = diferida(() => import('./screens/Tarificador'), 'Tarificador');
const Accesos = diferida(() => import('./screens/bandeja/Accesos'), 'Accesos');

// Tras publicar una version nueva, los archivos de pantallas de la version anterior ya no existen: se recarga una vez
// (no mas de una por minuto, para no entrar en un ciclo si el servidor falla de verdad).
window.addEventListener('vite:preloadError', (e) => {
  try {
    const antes = Number(sessionStorage.getItem('consola.recargaVersion') ?? 0);
    if (Date.now() - antes < 60_000) return;
    sessionStorage.setItem('consola.recargaVersion', String(Date.now()));
  } catch {
    return;
  }
  e.preventDefault();
  location.reload();
});

// 3CX abre la ficha del contacto con la ruta de Twenty (<CRM_URL_PUBLICA>/object/person/<id>): se lleva a la de la Consola.
function DesdeTwenty({ a }: { a: 'personas' | 'empresas' }) {
  const { id } = useParams();
  return <Navigate to={`/${a}/${id}`} replace />;
}

// Direcciones viejas (enlaces guardados): llevan a la nueva conservando lo que viene despues del ?.
function Redirigir({ a }: { a: string }) {
  const { search } = useLocation();
  return <Navigate to={`${a}${search}`} replace />;
}

// Pantallas con permiso (D99, lib/permisos.ts): sin el, a Hoy.
function ConPermiso({ clave, children }: { clave: string; children: ReactNode }) {
  const { puede, cargandoYo } = useSession();
  if (cargandoYo) return null;
  return puede(clave) ? <>{children}</> : <Navigate to="/hoy" replace />;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <Shell />,
    children: [
      // Ruta sin direccion propia: si una pantalla falla, el error se muestra dentro del marco (menu y telefono siguen).
      {
        errorElement: <ErrorDePantalla />,
        children: [
          { index: true, element: <Navigate to="/hoy" replace /> },
          { path: 'hoy', element: <Hoy /> },
          { path: 'agenda', element: <Agenda /> },
          { path: 'contactos', element: <Contactos fijo="personas" /> },
          { path: 'empresas', element: <Contactos fijo="empresas" /> },
          { path: 'personas/:id', element: <PersonaFicha /> },
          { path: 'empresas/:id', element: <EmpresaFicha /> },
          { path: 'object/person/:id', element: <DesdeTwenty a="personas" /> },
          { path: 'object/company/:id', element: <DesdeTwenty a="empresas" /> },
          { path: 'ventas', element: <ResumenVentas /> },
          {
            path: 'oportunidades',
            element: (
              <ConPermiso clave="oportunidades.ver">
                <Oportunidades />
              </ConPermiso>
            ),
          },
          { path: 'proyectos', element: <Proyectos /> },
          { path: 'proyectos/:id', element: <Proyectos /> },
          { path: 'horas', element: <MisHoras /> },
          {
            path: 'horas-equipo',
            element: (
              <ConPermiso clave="horas.equipo">
                <HorasEquipo />
              </ConPermiso>
            ),
          },
          { path: 'bolsas', element: <Bolsas /> },
          {
            path: 'licencias-3cx',
            element: (
              <ConPermiso clave="licencias3cx.ver">
                <Licencias3cx />
              </ConPermiso>
            ),
          },
          {
            path: 'licencias-3cx/renovaciones',
            element: (
              <ConPermiso clave="licencias3cx.ver">
                <Renovaciones3cx />
              </ConPermiso>
            ),
          },
          {
            path: 'reportes/casos',
            element: (
              <ConPermiso clave="reportes.casos">
                <CasosReporte />
              </ConPermiso>
            ),
          },
          {
            path: 'reportes/areas',
            element: (
              <ConPermiso clave="reportes.casos">
                <AreasReporte />
              </ConPermiso>
            ),
          },
          {
            path: 'reportes/horas',
            element: (
              <ConPermiso clave="reportes.horas">
                <HorasReporte />
              </ConPermiso>
            ),
          },
          {
            path: 'reportes/bolsas',
            element: (
              <ConPermiso clave="reportes.bolsas">
                <BolsasReporte />
              </ConPermiso>
            ),
          },
          {
            path: 'marketing/campanas',
            element: (
              <ConPermiso clave="campanas.ver">
                <Campanas />
              </ConPermiso>
            ),
          },
          {
            path: 'reportes/correo',
            element: (
              <ConPermiso clave="reportes.correo">
                <CorreoReporte />
              </ConPermiso>
            ),
          },
          { path: 'llamadas', element: <Llamadas /> },
          { path: 'bandeja', element: <Navigate to="/bandeja/correos" replace /> },
          { path: 'bandeja/correos', element: <Correos /> },
          { path: 'bandeja/casos', element: <Casos /> },
          { path: 'bandeja/conocimiento', element: <Conocimiento /> },
          { path: 'bandeja/accesos', element: <Accesos /> },
          { path: 'tareas', element: <Tareas /> },
          {
            path: 'equipo',
            element: (
              <ConPermiso clave="equipo.hoy">
                <Supervisor />
              </ConPermiso>
            ),
          },
          {
            path: 'reportes/3cx/:tablero?',
            element: (
              <ConPermiso clave="reportes.3cx">
                <Reportes3cx />
              </ConPermiso>
            ),
          },
          {
            path: 'costos',
            element: (
              <ConPermiso clave="reportes.costos">
                <Tarificador />
              </ConPermiso>
            ),
          },
          { path: 'supervisor', element: <Redirigir a="/equipo" /> },
          { path: 'tarificador', element: <Redirigir a="/costos" /> },
          { path: 'administracion', element: <Administracion /> },
          { path: 'administracion/:seccion', element: <Administracion /> },
          { path: 'perfil', element: <Ajustes /> },
          { path: 'ajustes', element: <Redirigir a="/perfil" /> },
          { path: '*', element: <NoEncontrado /> },
        ],
      },
    ],
  },
]);

export function App() {
  const session = useSesionActiva();
  const qc = useQueryClient();
  // Al cambiar de empresa o salir, no se mezclan datos en cache (no en la primera carga:
  // limpiar ahi dejaria sin respuesta a las consultas que ya estan en curso).
  const clave = session ? `${session.workspace.id ?? session.workspace.nombre}-${session.modo}` : '';
  const anterior = useRef(clave);
  const clavePersistencia = claveDeSesion(session) ?? '';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const persistencia = useMemo(() => opcionesPersistencia(session), [clavePersistencia]);
  useEffect(() => {
    if (anterior.current !== clave) qc.clear();
    anterior.current = clave;
  }, [clave, qc]);
  // Enlaces de los correos del CRM: funcionan con o sin sesion abierta (D24).
  const ruta = window.location.pathname;
  if (ruta.startsWith('/invite/')) return <Invitacion />;
  if (ruta.startsWith('/reset-password/')) return <NuevaClave />;
  if (!session) return <Login />;
  const contenido = (
    <SessionProvider session={session} key={clave}>
      <RouterProvider router={router} />
    </SessionProvider>
  );
  // Copia de las consultas livianas en el navegador (por empresa y persona; no en el puesto compartido).
  return persistencia ? (
    <PersistQueryClientProvider key={clavePersistencia} client={qc} persistOptions={persistencia}>
      {contenido}
    </PersistQueryClientProvider>
  ) : (
    contenido
  );
}
